import { useState, useEffect } from 'react'
import { FolderTree, ChevronRight } from 'lucide-react'
import { useEvidenceStore } from '../store/evidenceStore'
import { useAuthStore } from '../store/authStore'
import CriterioPanel from './CriterioPanel'

const CriterioSelectorView = () => {
  const [selectedDimension, setSelectedDimension] = useState('')
  const [selectedCriterio, setSelectedCriterio] = useState('')
  
  const { formStructure, fetchFormStructure, isLoadingStructure } = useEvidenceStore()
  const { user } = useAuthStore()

  useEffect(() => {
    if (!formStructure) {
      fetchFormStructure(false, user?.email)
    }
  }, [formStructure, fetchFormStructure, user?.email])

  const handleDimensionChange = (dimension) => {
    setSelectedDimension(dimension)
    setSelectedCriterio('') // Reset criterio when dimension changes
  }

  const criterios = selectedDimension && formStructure 
    ? (formStructure[selectedDimension] || []).map(c => 
        typeof c === 'object' ? c.nombre : c
      ) 
    : []

  return (
    <div className="space-y-6">
      {/* Selector de Foco y Criterio */}
      <div className="card">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Mejora Continua
          </h2>
          <p className="text-gray-600">
            Selecciona un foco y criterio para gestionar brechas y ciclos de mejora PDCA
          </p>
        </div>

        {isLoadingStructure ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Cargando estructura...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Selector de Foco/Dimensión */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                1. Selecciona el Foco <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedDimension}
                onChange={(e) => handleDimensionChange(e.target.value)}
                className="input-field"
              >
                <option value="">-- Selecciona un foco --</option>
                {formStructure && Object.keys(formStructure).map((dimension) => (
                  <option key={dimension} value={dimension}>
                    {dimension}
                  </option>
                ))}
              </select>
            </div>

            {/* Selector de Criterio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                2. Selecciona el Criterio <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedCriterio}
                onChange={(e) => setSelectedCriterio(e.target.value)}
                disabled={!selectedDimension}
                className="input-field disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">
                  {selectedDimension ? '-- Selecciona un criterio --' : '-- Primero selecciona un foco --'}
                </option>
                {criterios.map((criterio) => (
                  <option key={criterio} value={criterio}>
                    {criterio}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Breadcrumb de selección */}
        {(selectedDimension || selectedCriterio) && (
          <div className="mt-4 flex items-center space-x-2 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
            <FolderTree className="h-4 w-4 text-blue-600" />
            <span className="font-medium text-blue-900">Selección actual:</span>
            {selectedDimension && (
              <>
                <span className="text-blue-700">{selectedDimension}</span>
                {selectedCriterio && (
                  <>
                    <ChevronRight className="h-4 w-4 text-blue-400" />
                    <span className="text-blue-700 font-medium">{selectedCriterio}</span>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Panel del Criterio Seleccionado */}
      {selectedDimension && selectedCriterio ? (
        <CriterioPanel 
          criterio={{ 
            nombre: selectedCriterio,
            dimension: selectedDimension
          }}
          criterioId={1} // TODO: Obtener el ID real del criterio desde la estructura
        />
      ) : (
        /* Estado vacío - Esperando selección */
        <div className="card text-center py-16">
          <FolderTree className="mx-auto h-16 w-16 text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Selecciona un Foco y Criterio
          </h3>
          <p className="text-gray-600 max-w-md mx-auto">
            Usa los selectores de arriba para elegir el foco y criterio que deseas gestionar.
            Podrás ver sus evidencias de cumplimiento y registrar brechas de mejora continua.
          </p>
        </div>
      )}
    </div>
  )
}

export default CriterioSelectorView
